import type { ChatLogEntry } from '@pixelworld/shared';
import { useEffect, useState } from 'react';
import { Button } from '../../components/Button';
import { da } from '../../i18n/da';
import { fetchChatLog } from '../auth/auth-api';
import styles from './ChatLogPanel.module.css';

const time = new Intl.DateTimeFormat('da-DK', { hour: '2-digit', minute: '2-digit' });

/**
 * For the admin: what was written, also what the filter stopped, so a player who tries
 * to share contact details can be blocked (ADR 0008). In open rooms, it shows which
 * host let the player write (ADR 0011).
 */
export function ChatLogPanel() {
  const [entries, setEntries] = useState<ChatLogEntry[] | null>(null);
  const [failed, setFailed] = useState(false);
  // Bumped by the refresh button to load the log again.
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let active = true;
    const load = async () => {
      const result = await fetchChatLog();
      if (!active) return;
      setFailed(!result.ok);
      if (result.ok) setEntries(result.data.entries);
    };
    void load();
    return () => {
      active = false;
    };
  }, [version]);

  return (
    <section className={styles.panel}>
      <div className={styles.header}>
        <strong>{da.admin.chatTitle}</strong>
        <Button size="small" variant="quiet" onClick={() => setVersion((value) => value + 1)}>
          {da.admin.chatRefresh}
        </Button>
      </div>
      {failed && <p className={styles.note}>{da.admin.chatLoadFailed}</p>}
      {entries?.length === 0 && <p className={styles.note}>{da.admin.chatEmpty}</p>}
      {entries && entries.length > 0 && (
        <ul className={styles.list} data-testid="chat-log">
          {entries.map((entry, index) => (
            <li
              key={`${entry.sentAt}-${index}`}
              className={entry.rejected ? styles.stopped : undefined}
            >
              <span className={styles.meta}>
                {time.format(entry.sentAt)} · {entry.room} · {entry.username}
                {entry.approvedBy && ` · ${da.admin.chatApprovedBy(entry.approvedBy)}`}
              </span>
              <span>
                {entry.rejected && <em className={styles.tag}>{da.admin.chatStopped}</em>}{' '}
                {entry.text}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
